import React, { useMemo } from 'react'
import type { EChartsOption } from 'echarts'
import EChart from './EChart'
import { abbreviateNumber } from './theme'

// Abbreviated value-axis labels (1.2k, 3.4M) shared across the charts.
const valueAxisLabel = { formatter: (v: number) => abbreviateNumber(v) }

// Projection bar width as a fraction of the category band, and the white gap (px)
// left between the within-budget and over-target segments for the target line to
// run through. BAR_FRACTION matches the Footprint chart's bars (theme barCategoryGap
// 42% -> bars fill 58% of the band) so the two charts read as one family.
const BAR_FRACTION = 0.58
const TARGET_GAP_PX = 9
// Minimum red-cap height (px). A tiny overshoot would otherwise render as a hairline
// that tapers to a point; we let the VISUAL bar top rise so the cap is at least this
// tall - a small, deliberate break of accuracy. The tooltip still shows true numbers.
const MIN_RED_PX = 8
// Outer corner radius for the projection bars, matching the Footprint theme's
// borderRadius: 6. Only the axis-aligned outer ends are rounded (the slanted target
// cut stays crisp).
const BAR_CORNER = 6

// Points along a circular arc, used to build bar shapes with rounded corners.
const arcPts = (cx: number, cy: number, r: number, a0: number, a1: number, steps = 4): number[][] => {
  const pts: number[][] = []
  for (let s = 0; s <= steps; s++) {
    const a = ((a0 + ((a1 - a0) * s) / steps) * Math.PI) / 180
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)])
  }
  return pts
}

// The gas domain palette for the few-series stacked emissions chart. Order matches
// the gas series CH₄ / CO₂ / N₂O and is fixed so each gas keeps its colour on the
// bars and in the legend. The original Tellus blue ramp: light -> bright -> navy,
// so visual weight tracks magnitude.
const EMISSIONS_COLORS = ['#DEAC50', '#0067a5', '#2e7a54', '#99bdd3', '#a5bcca'] // CH₄ amber / CO₂ blue / N₂O green, then fallbacks for any extra series

// ---------------------------------------------------------------------------
// The suite's ECharts charts, themed from the brand tokens: the emissions
// stacked bar and the projections actual-vs-target. Consumed by the
// EmissionsStackedChart / ProjectionsChart side-table wrappers.
// ---------------------------------------------------------------------------

interface Row {
  [key: string]: string | number
}

// 1. Emissions stacked columns (replaces HighchartsStackedAreaChart).
// data: [{ year, <seriesA>: n, <seriesB>: n, ... }]
export const EmissionsStackedAreaEChart = ({
  data,
  xKey = 'year',
  yAxisLabel = 'Total emitted (tCO₂e)',
  unit = 'tCO₂e',
  colors,
  height = 460,
}: {
  data: Row[]
  xKey?: string
  yAxisLabel?: string
  unit?: string
  colors?: string[]
  height?: number
}) => {
  const option = useMemo<EChartsOption>(() => {
    const seriesKeys = data.length ? Object.keys(data[0]).filter((k) => k !== xKey) : []
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v) => `${Number(v).toLocaleString()} ${unit}` },
      // The unit lives once on the y-axis (it applies to the whole scale); the legend just
      // names the series, so it doesn't repeat "· tCO2e" on every gas.
      legend: { bottom: 24 },
      // Shared chart margins (kept identical to the Projections chart so the two read
      // as one family). containLabel reserves space for the tick labels; left gives the
      // rotated unit room; right leaves space so both charts align even though only the
      // Projections chart uses its right margin for the "Target" tag.
      grid: { top: 40, left: 42, right: 70, bottom: 76, containLabel: true },
      xAxis: { type: 'category', data: data.map((d) => String(d[xKey])) },
      yAxis: { type: 'value', name: unit, nameLocation: 'middle', nameGap: 48, nameTextStyle: { color: '#6f8998', fontSize: 11 }, axisLabel: valueAxisLabel },
      // A handful of series with clearly-distinct solid blues (navy -> bright ->
      // light) - so they read apart by colour without needing texture.
      color: colors && colors.length ? colors : EMISSIONS_COLORS,
      series: seriesKeys.map((key) => ({
        name: key,
        type: 'bar',
        stack: 'total',
        // No hover emphasis: focus:'series' dimmed the other stacked series on hover and
        // cleared it in the gaps between bars, so sweeping across the chart flickered
        // (one series highlighted -> both -> one). The axis tooltip already gives the
        // hover readout, so the dim was redundant.
        emphasis: { disabled: true },
        data: data.map((d) => Number(d[key]) || 0),
      })),
    }
  }, [data, xKey, yAxisLabel, unit, colors])

  return <EChart option={option} height={height} notMerge decals={false} data-testid="echart-emissions" />
}

// 2. Projections bullet (replaces HighchartsBulletChart).
// data: [{ year, target, actual }] - subtle target range bar + actual bar + target line marker.
export const ProjectionsBulletEChart = ({
  data,
  targetLabel = 'Target',
  actualLabel = 'Actual',
  unit = 'tCO₂e',
  height = 460,
}: {
  data: { year?: string | number; target?: number; actual?: number }[]
  targetLabel?: string
  actualLabel?: string
  unit?: string
  height?: number
}) => {
  const option = useMemo<EChartsOption>(() => {
    const categories = data.map((d) => String(d.year))
    const targets = data.map((d) => Number(d.target) || 0)
    // Split each projected bar at its target: the part within budget (blue) and the
    // overshoot above it (red). The target is then drawn ONCE as a continuous line
    // sweeping across the years - a descending budget trajectory - so an overshoot
    // reads as the bar poking above the line, not a tick buried inside every bar.
    const withinTarget = data.map((d) => Math.min(Number(d.actual) || 0, Number(d.target) || 0))
    const overTarget = data.map((d) => Math.max(0, (Number(d.actual) || 0) - (Number(d.target) || 0)))
    const fmt = (v: number) => `${Math.round(Number(v)).toLocaleString()} ${unit}`
    const swatch = (c: string) => `<span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${c};margin-right:7px;vertical-align:middle"></span>`
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        // Custom rows: "Projected Emissions" shows the FULL projected total (within +
        // over), not just the capped blue segment, and "Over target" appears only when
        // there is an overshoot.
        formatter: (params: any) => {
          const i = Array.isArray(params) ? params[0]?.dataIndex ?? 0 : params.dataIndex
          const over = overTarget[i]
          const actual = withinTarget[i] + over
          return [
            `<div style="font-weight:600;margin-bottom:4px">${categories[i]}</div>`,
            `${swatch('#0084d6')}${actualLabel}: <b>${fmt(actual)}</b>`,
            over > 0 ? `${swatch('#D83527')}Over target: <b>${fmt(over)}</b>` : '',
            `${swatch('#0b2a3d')}${targetLabel}: <b>${fmt(targets[i])}</b>`,
          ].filter(Boolean).join('<br/>')
        },
      },
      legend: { bottom: 24, data: [actualLabel, 'Over target', targetLabel] },
      // Shared chart margins (identical to the Footprint chart). The right margin holds
      // the "Target" tag at the end of the trajectory line.
      grid: { top: 40, left: 42, right: 70, bottom: 76, containLabel: true },
      xAxis: { type: 'category', data: categories, boundaryGap: true },
      yAxis: { type: 'value', name: unit, nameLocation: 'middle', nameGap: 48, nameTextStyle: { color: '#6f8998', fontSize: 11 }, axisLabel: valueAxisLabel },
      series: [
        // The bars, drawn as a custom series so a white GAP can be left at the target
        // level for the trajectory line to run through. Each bar splits FLAT at its
        // target: within-budget blue below, overshoot red above, with a fixed pixel gap
        // between the two that the line + dot thread through (see the target sketch).
        {
          type: 'custom',
          name: 'projection-bars',
          z: 2,
          silent: true,
          data: targets.map((_, i) => i),
          renderItem: (params: any, api: any) => {
            const i = params.dataIndex
            const n = targets.length
            const bandW = api.size([1, 0])[0]
            const halfBar = (bandW * BAR_FRACTION) / 2
            const xc = api.coord([i, 0])[0]
            const y0 = api.coord([i, 0])[1]
            const actual = withinTarget[i] + overTarget[i]
            const yActual = api.coord([i, actual])[1]
            // Target at the bar's left/right edges, interpolated (and extrapolated at the
            // ends), so the gap between blue and red is slanted at the SAME angle as the
            // target line running through it.
            const tgtAt = (p: number) => {
              let lo = Math.floor(p)
              if (lo < 0) lo = 0
              if (lo > n - 2) lo = n - 2
              return targets[lo] + (p - lo) * (targets[lo + 1] - targets[lo])
            }
            const half = BAR_FRACTION / 2
            const yTgtL = api.coord([i, tgtAt(i - half)])[1]
            const yTgtR = api.coord([i, tgtAt(i + half)])[1]
            const xL = xc - halfBar
            const xR = xc + halfBar
            // White border matches the Footprint bars (theme borderColor #fff / width 3).
            const border = { stroke: '#ffffff', lineWidth: 3, lineJoin: 'round' as const }
            // Red is decided by the DATA (any over-target year), never by whether the gap
            // fits - otherwise a small overshoot would silently vanish and the bar would
            // look "complete" at the line.
            const hasRed = overTarget[i] > 0
            const children: any[] = []
            if (hasRed) {
              // A FIXED half-gap keeps the white band the SAME size on every bar. A small
              // overshoot still shows red: the red tapers to a point where the bar barely
              // clears target (the clamp below), rather than the gap shrinking with it.
              const g = TARGET_GAP_PX / 2
              // Blue: crisp slanted top half a gap below the target, rounded bottom.
              const blueL = yTgtL + g
              const blueR = yTgtR + g
              const rb = Math.max(0, Math.min(BAR_CORNER, (y0 - Math.max(blueL, blueR)) / 2))
              children.push({
                type: 'polygon',
                shape: {
                  points: [
                    [xL, blueL],
                    [xR, blueR],
                    ...arcPts(xR - rb, y0 - rb, rb, 0, 90),
                    ...arcPts(xL + rb, y0 - rb, rb, 90, 180),
                  ],
                },
                style: { fill: '#0084d6', ...border },
              })
              // Red: slanted bottom half a gap above the target. The visual top rises to
              // keep the shallow edge at least MIN_RED_PX tall, so a tiny overshoot reads
              // as a clean small cap instead of a tapering hairline.
              const redL = yTgtL - g
              const redR = yTgtR - g
              const yTop = Math.min(yActual, Math.min(redL, redR) - MIN_RED_PX)
              // Round each top corner by its own available height (both now >= MIN_RED_PX).
              const rtL = Math.max(0, Math.min(BAR_CORNER, (redL - yTop) / 2))
              const rtR = Math.max(0, Math.min(BAR_CORNER, (redR - yTop) / 2))
              children.push({
                type: 'polygon',
                shape: {
                  points: [
                    ...arcPts(xL + rtL, yTop + rtL, rtL, 180, 270),
                    ...arcPts(xR - rtR, yTop + rtR, rtR, 270, 360),
                    [xR, redR],
                    [xL, redL],
                  ],
                },
                style: { fill: '#D83527', ...border },
              })
            } else {
              // Within-budget year: a COMPLETE blue bar with all four corners rounded (its
              // top is the real emission, below the target line), so it reads as finished
              // rather than cut off. The target line simply floats above it.
              const r = Math.max(0, Math.min(BAR_CORNER, (y0 - yActual) / 2))
              children.push({
                type: 'polygon',
                shape: {
                  points: [
                    ...arcPts(xL + r, yActual + r, r, 180, 270),
                    ...arcPts(xR - r, yActual + r, r, 270, 360),
                    ...arcPts(xR - r, y0 - r, r, 0, 90),
                    ...arcPts(xL + r, y0 - r, r, 90, 180),
                  ],
                },
                style: { fill: '#0084d6', ...border },
              })
            }
            return { type: 'group', children }
          },
        },
        // The target trajectory drawn on top: the continuous descending line (coincident
        // with the diagonal cut), a dot at each year, and a "Target" tag at the end.
        {
          name: targetLabel,
          type: 'line',
          data: targets,
          smooth: false,
          symbol: 'circle',
          symbolSize: 7,
          showSymbol: true,
          itemStyle: { color: '#0b2a3d', borderColor: '#fff', borderWidth: 1.5 },
          lineStyle: { color: '#0b2a3d', width: 2.5, cap: 'round', join: 'round' },
          emphasis: { disabled: true },
          endLabel: { show: true, formatter: 'Target', color: '#0b2a3d', fontWeight: 700, fontSize: 12, offset: [16, 0] },
          z: 6,
        },
        // Legend swatches only (no data): the custom series can't supply the two bar
        // colours to the legend, so these carry "Projected Emissions" (blue) and "Over
        // target" (red). The line series above carries the "Target" entry.
        { name: actualLabel, type: 'bar', data: [], itemStyle: { color: '#0084d6' }, silent: true },
        { name: 'Over target', type: 'bar', data: [], itemStyle: { color: '#D83527' }, silent: true },
      ],
    }
  }, [data, targetLabel, actualLabel, unit])

  return <EChart option={option} height={height} notMerge decals={false} data-testid="echart-projections" />
}
